/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, UseFormSetError } from 'react-hook-form';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  Group,
  Input,
  InputAddon,
  Menu,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react';

import {
  CaretDown,
  LinkSimpleHorizontal,
  Microphone,
  WarningCircle,
  X
} from '@phosphor-icons/react';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { addMinutes } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

import { useTimezoneSelect, allTimezones, ITimezoneOption } from 'react-timezone-select';

import { fetchAPI } from '@/api/fetch';
import { apiGetUnscheduledCandidates, apiGetAccountProfile } from '@/api/services';
import { apiCreateMeeting, apiUpdateMeeting } from '@/api/services/meeting.service';

import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@/components/ui/dialog';

import { Field } from '@/components/ui/field';
import { RequiredFieldLabel } from '@/components/forms/RequiredFieldLabel';

import { CandidateWithJobContext, CandidateWithJobContextBasicInfo } from '@/types/Candidate';
import { Job, JobBasicInfo } from '@/types/Job';
import { Meeting, MeetingSummary } from '@/types/Meeting';

import {
  AgencyPlan,
  AgencyUsageType,
  JobCandidateStatus,
  EmploymentType,
  JobStatus,
  WorkType,
  CandidateExperienceLevel,
  CandidateStatus,
  CandidateSource
} from '@/types/enums';

import { LimitExhaustedDialog } from '../Agent/LimitExhaustedDialog';

import { MAX_MEETING_TIME_PER_MEETING, MAX_MEETING_TIME_TOTAL } from '@/config';

import { calculateTimestamp, getCandidateFullName } from '@/utils';
import { Client } from '@/types/Client';

// Types
interface FormValues {
  isLive: boolean;
  meeting_date: Date | null;
  meeting_time_from: Date | null;
  meeting_time_to: Date | null;
  timezone: { value: ITimezoneOption };
  meeting_url: string;
  title: string;
  client?: { value: Client };
  job?: { value: Job };
  candidate?: { value: CandidateWithJobContextBasicInfo };
}

interface AddInterviewDialogProps {
  mode: 'add' | 'add-candidate' | 'edit';
  meeting?: MeetingSummary;
  onAddInterview?: () => void;
  onEditInterview?: (meeting: MeetingSummary) => void;
  isOpen?: boolean;
  onClose?: () => void;
  job?: Job;
  candidate?: CandidateWithJobContext;
  plan: AgencyPlan;
  isExhausted?: boolean;
  triggerText?: string;
}

// Constants
const DEFAULT_MEETING_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
const ZOOM_URL_REGEX = /^https:\/\/.*\.zoom\.us\/.*$/;
const GOOGLE_MEET_URL_REGEX = /^(https:\/\/)?meet\.google\.com\/.*$/;
const MS_TEAMS_URL_REGEX = /^https:\/\/teams\.microsoft\.com\/.*$/;

const ErrorMessage = ({ message }: { message?: string }) =>
  message ? (
    <Box fontSize="14px" color="error.700" role="alert">
      {message}
    </Box>
  ) : null;

const TimeLabel = ({ children }: { children: React.ReactNode }) => (
  <Box
    w="100%"
    border="1px solid"
    borderRight="none"
    borderLeftRadius="8px"
    borderColor="neutrals.200"
    p="10px 16px"
    fontSize="16px"
  >
    {children}
  </Box>
);

// Form Validation
const validateMeetingTime = (
  meeting_date: Date | null,
  meeting_time_from: Date | null,
  meeting_time_to: Date | null,
  timezone: ITimezoneOption,
  mode: 'add' | 'add-candidate' | 'edit',
  setError: UseFormSetError<FormValues>
): boolean => {
  if (!meeting_date || !meeting_time_from || !meeting_time_to) return false;

  const meeting_start_timestamp = calculateTimestamp(meeting_date, meeting_time_from, timezone);
  const currentTimestamp = Math.floor(Date.now() / 1000) + (mode === 'add' ? 60 : 0);

  if (meeting_start_timestamp < currentTimestamp) {
    setError('meeting_date', {
      type: 'manual',
      message: 'Meeting date and time cannot be in the past'
    });
    setError('meeting_time_from', {
      type: 'manual',
      message: 'Meeting start time cannot be in the past'
    });
    setError('meeting_time_to', {
      type: 'manual',
      message: 'Meeting end time cannot be in the past'
    });
    return false;
  }

  const fromHours = meeting_time_from.getHours();
  const fromMinutes = meeting_time_from.getMinutes();
  const toHours = meeting_time_to.getHours();
  const toMinutes = meeting_time_to.getMinutes();

  const fromTotalMinutes = fromHours * 60 + fromMinutes;
  const toTotalMinutes = toHours * 60 + toMinutes;

  const isOvernight = fromHours >= 22 && toHours <= 2;

  if (!isOvernight && toTotalMinutes <= fromTotalMinutes) {
    setError('meeting_time_to', {
      type: 'manual',
      message: 'End time must be after start time'
    });
    return false;
  }

  return true;
};

// Main Component
export default function AddInterviewDialog({
  mode,
  meeting,
  onAddInterview,
  onEditInterview,
  isOpen = false,
  onClose,
  job,
  candidate,
  plan,
  isExhausted = false,
  triggerText = 'Add a Session'
}: AddInterviewDialogProps) {
  // State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isError, setIsError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [jobs, setJobs] = useState<JobBasicInfo[]>([]);
  const [candidates, setCandidates] = useState<CandidateWithJobContext[]>([]);
  const [isLoadingCandidate, setIsLoadingCandidate] = useState(false);
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  const [isFreePlan, setIsFreePlan] = useState(false);
  const planNotAvailable = (mode === 'add' || mode === 'add-candidate') && plan === AgencyPlan.FREE;

  const noCandidates = useMemo(() => {
    return mode === 'add' && !isLoadingCandidate && clients.length === 0;
  }, [mode, isLoadingCandidate, clients]);

  // Timezone setup
  const { options, parseTimezone } = useTimezoneSelect({
    timezones: allTimezones,
    labelStyle: 'original'
  });

  // Form setup
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      isLive: false,
      meeting_date: null,
      meeting_time_from: null,
      meeting_time_to: null,
      timezone: {
        value: parseTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
      },
      meeting_url: '',
      title: '',
      client: undefined,
      job: undefined,
      candidate: undefined
    }
  });

  // Effects
  useEffect(() => {
    if (mode === 'add' && isDialogOpen) {
      initializeMeetingDetails();
    }
  }, [isDialogOpen, mode]);

  useEffect(() => {
    if (mode === 'add-candidate' && isDialogOpen) {
      initializeMeetingDetails();

      if (job && candidate) {
        setValue('job', { value: job });
        setValue('client', { value: job.client });
        setValue('candidate', {
          value: {
            candidate_id: candidate.candidate_id,
            unique_id: candidate.unique_id,
            first_name: candidate.first_name,
            job: {
              job_id: job.job_id,
              unique_id: job.unique_id,
              job_title: job.job_title,
              location: job.location,
              work_type: job.work_type,
              employment_type: job.employment_type,
              client: job.client,
              status: job.status,
              created_at: job.created_at,
              timestamp: job.timestamp,
              candidates: [],
              category: '',
              publish_job_board: false
            },
            email: candidate.email,
            phone: candidate.phone,
            linkedin: candidate.linkedin,
            overall_score: candidate.overall_score,
            system_score: candidate.system_score,
            original_resume_file_name: candidate.original_resume_file_name,
            user_score: {
              resume_score: 0,
              interview_score: 0,
              overall_score: 0
            },
            resume_data: candidate.resume_data,
            status: candidate.status,
            candidate_status: candidate.candidate_status,
            created_at: candidate.created_at,
            experience_level: CandidateExperienceLevel.EntryLevel,
            source: CandidateSource.RecruiterUpload,
            last_name: '',
            allow_delete: false,
            application_answers: []
          }
        });
      }
    }
  }, [isDialogOpen, mode, job, candidate]);

  useEffect(() => {
    if (mode === 'edit' && isOpen && meeting) {
      initializeEditMeetingDetails();
    }
  }, [isOpen, meeting, mode]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setIsLoadingCandidate(true);
        const response = await fetchAPI<Client[]>({
          request: () => apiGetUnscheduledCandidates()
        });

        if (response.success) {
          setClients(response.data);
        } else {
          console.error('Error fetching clients:', response.message);
          setIsError('Failed to fetch clients. Please try again later.');
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        setIsError('Failed to fetch clients. Please try again later.');
      } finally {
        setIsLoadingCandidate(false);
      }
    };

    if (mode === 'add' && isDialogOpen) {
      fetchClients();
    }
  }, [isDialogOpen, mode]);

  // Helper functions
  const initializeMeetingDetails = () => {
    setIsError(null);
    const fromTime = new Date(new Date().setHours(new Date().getHours() + 1, 0, 0, 0));
    const toTime = new Date(fromTime.getTime() + DEFAULT_MEETING_DURATION);

    setValue('isLive', true);
    setValue('meeting_date', new Date());
    setValue('meeting_time_from', fromTime);
    setValue('meeting_time_to', toTime);
    setValue('timezone', {
      value: parseTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
    });
  };

  const initializeEditMeetingDetails = () => {
    if (!meeting) return;

    setIsError(null);
    const startDate = new Date(meeting.meeting_start_time * 1000);
    const endDate = new Date(meeting.meeting_end_time * 1000);

    setValue('meeting_date', startDate);
    setValue('meeting_time_from', startDate);
    setValue('meeting_time_to', endDate);
    setValue('timezone', {
      value: parseTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
    });
    setValue('client', { value: meeting.candidate?.job.client });
    setValue('job', {
      value: {
        job_id: meeting.candidate?.job.job_id || '',
        job_title: meeting.candidate?.job.job_title || '',
        unique_id: meeting.candidate?.job.unique_id || '',
        location: meeting.candidate?.job.location || '',
        currency: '',
        salary_from: 0,
        salary_to: 0,
        work_type: WorkType.Onsite,
        employment_type: EmploymentType.FullTime,
        description: '',
        contact_name: '',
        contact_role: '',
        contact_email: '',
        contact_phone: '',
        jd_file_url: null,
        requirements: null,
        status: JobStatus.InProgress,
        created_at: 0,
        timestamp: meeting.candidate?.job.timestamp || {
          published_at: null,
          archived_at: null,
          completed_at: null
        },
        client: meeting.candidate?.job.client,
        position_tags: [],
        category: '',
        publish_job_board: false,
        application_questions: []
      }
    });
    setValue('candidate', {
      value: {
        candidate_id: meeting.candidate?.candidate_id || '',
        unique_id: meeting.candidate?.unique_id || '',
        first_name: meeting.candidate?.first_name || '',
        last_name: meeting.candidate?.last_name || '',
        job: meeting.candidate?.job || {
          job_id: '',
          unique_id: '',
          job_title: '',
          location: '',
          work_type: WorkType.Onsite,
          employment_type: EmploymentType.FullTime,
          client: {
            client_id: '',
            name: '',
            company_name: '',
            email: '',
            phone: '',
            website: ''
          },
          status: JobStatus.InProgress,
          created_at: 0,
          timestamp: {
            published_at: null,
            archived_at: null,
            completed_at: null
          },
          candidates: [],
          category: '',
          publish_job_board: false
        },
        email: meeting.candidate?.email || '',
        phone: meeting.candidate?.phone || '',
        linkedin: meeting.candidate?.linkedin || '',
        overall_score: meeting.candidate?.overall_score || 0,
        system_score: meeting.candidate?.system_score || 0,
        original_resume_file_name: meeting.candidate?.original_resume_file_name || '',
        status: meeting.candidate?.status || JobCandidateStatus.New,
        candidate_status: meeting.candidate?.candidate_status || CandidateStatus.ResumeExtracting,
        created_at: meeting.candidate?.created_at || 0,
        user_score: {
          resume_score: 0,
          interview_score: 0,
          overall_score: 0
        },
        resume_data: undefined,
        experience_level:
          meeting.candidate?.experience_level || CandidateExperienceLevel.EntryLevel,
        source: meeting.candidate?.source || CandidateSource.RecruiterUpload,
        allow_delete: false,
        application_answers: []
      }
    });

    setValue('meeting_url', meeting.meeting_url || '');
    setValue('title', meeting?.title || '');
  };

  const calculateTimestamp = (date: Date | null, time: Date | null) => {
    if (!date || !time) return 0;

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hours = time.getHours();
    const minutes = time.getMinutes();

    const combined = new Date(year, month, day, hours, minutes, 0);

    const zonedDate = fromZonedTime(combined, watch('timezone').value.value);
    return zonedDate.getTime() / 1000;
  };

  // Handlers
  const handleFormSubmit = async (formData: FormValues) => {
    const {
      meeting_url,
      candidate,
      job,
      isLive,
      meeting_date,
      meeting_time_from,
      meeting_time_to,
      title: initialTitle
    } = formData;

    if (
      !isLive &&
      !validateMeetingTime(
        meeting_date,
        meeting_time_from,
        meeting_time_to,
        watch('timezone').value,
        mode,
        setError
      )
    ) {
      return;
    }

    const title =
      initialTitle ||
      (mode === 'add' || mode === 'add-candidate'
        ? `Interview with ${getCandidateFullName(
            candidate?.value.first_name || '',
            candidate?.value.last_name || ''
          )}`
        : `Interview with ${getCandidateFullName(
            meeting?.candidate?.first_name || '',
            meeting?.candidate?.last_name || ''
          )}`);

    if (
      (mode === 'add' || mode === 'add-candidate') &&
      (!job?.value.job_id || !candidate?.value.candidate_id)
    ) {
      setIsError('No job or candidate selected. Please try again.');
      return;
    }

    const meeting_start_timestamp = calculateTimestamp(meeting_date, meeting_time_from);
    const meeting_end_timestamp = calculateTimestamp(meeting_date, meeting_time_to);

    try {
      if (mode === 'add' || mode === 'add-candidate') {
        const response = await fetchAPI<Meeting>({
          request: () =>
            apiCreateMeeting({
              jobId: job!.value.job_id,
              candidateId: candidate!.value.candidate_id,
              payload: {
                is_live: isLive,
                title,
                meeting_url,
                start_time: meeting_start_timestamp,
                end_time: meeting_end_timestamp
              }
            })
        });

        if (response.success) {
          onAddInterview?.();
          setIsDialogOpen(false);
        } else {
          setIsError('Failed to create meeting. Please try again.');
        }
      } else {
        // Edit mode
        const response = await fetchAPI<Meeting>({
          request: () =>
            apiUpdateMeeting(meeting!.meeting_id, {
              title,
              meeting_url,
              start_time: meeting_start_timestamp,
              end_time: meeting_end_timestamp
            })
        });

        if (response.success) {
          const updatedMeeting = response.data;
          onEditInterview?.({
            title: updatedMeeting.title,
            meeting_start_time: updatedMeeting.meeting_start_time,
            meeting_end_time: updatedMeeting.meeting_end_time,
            meeting_url: updatedMeeting.meeting_url,
            meeting_id: updatedMeeting.meeting_id,
            timestamp: updatedMeeting.timestamp,
            status: updatedMeeting.status,
            candidate: meeting?.candidate
          });
          onClose?.();
        } else {
          setIsError('Failed to update meeting. Please try again.');
        }
      }
    } catch (error) {
      console.error(`Error ${mode === 'add' ? 'creating' : 'updating'} meeting:`, error);
      setIsError(`Failed to ${mode === 'add' ? 'create' : 'update'} meeting. Please try again.`);
    }
  };

  const handleDialogClose = () => {
    reset();
    setIsError(null);
    if (mode === 'add') {
      setIsDialogOpen(false);
    } else {
      onClose?.();
    }
  };

  // Derived state
  const isLive = watch('isLive');
  const selectedClient = watch('client');
  const selectedJob = watch('job');

  // Handle button click with limit checking
  const handleAddSessionClick = async () => {
    // Fetch fresh account data to check latest limits
    try {
      const response = await fetchAPI<Account>({
        request: () => apiGetAccountProfile()
      });

      if (response.success && response.data) {
        const freshAccount = response.data;
        const meetingTimeUsage = freshAccount.agency?.usages?.find(
          (usage) => usage.type === AgencyUsageType.MeetingTime
        );
        const isExhausted = meetingTimeUsage?.exhausted || false;
        const isPlanFree = freshAccount.agency?.plan === AgencyPlan.FREE;

        if (isExhausted || isPlanFree) {
          setIsFreePlan(isPlanFree);
          setIsLimitDialogOpen(true);
        } else {
          setIsDialogOpen(true);
        }
      } else {
        // If API call fails, allow dialog to open
        setIsDialogOpen(true);
      }
    } catch {
      // If error occurs, allow dialog to open
      setIsDialogOpen(true);
    }
  };

  return (
    <>
      {(mode === 'add' || mode === 'add-candidate') && (
        <Button
          color="neutrals.0"
          bg="primary.900"
          borderRadius="8px"
          fontWeight="semibold"
          fontSize="14px"
          p="10px 24px"
          _hover={{ bg: 'primary.700' }}
          aria-label="Add a new session"
          onClick={handleAddSessionClick}
        >
          {triggerText}
          <Microphone size={24} aria-hidden="true" />
        </Button>
      )}
      <DialogRoot
        size="xl"
        open={mode === 'add' || mode === 'add-candidate' ? isDialogOpen : isOpen}
        onOpenChange={({ open }) => {
          if (mode === 'add' || mode === 'add-candidate') {
            setIsDialogOpen(open);
            if (open) {
              reset();
            }
          } else {
            if (open) {
              reset();
            }
            if (!open) {
              onClose?.();
            }
          }
        }}
        closeOnInteractOutside={false}
        placement="center"
        motionPreset="slide-in-bottom"
        onExitComplete={handleDialogClose}
        unmountOnExit
      >
      <DialogContent borderRadius="12px">
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogHeader>
            <DialogTitle>
              {mode === 'add' || mode === 'add-candidate' ? 'Add a Session' : 'Edit Session'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap="4">
              {planNotAvailable ? (
                <Flex
                  bg="error.50"
                  color="error.500"
                  gap="12px"
                  mb="8px"
                  w="100%"
                  p="12px"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="warning.200"
                  alignItems="center"
                >
                  <WarningCircle size={24} weight="fill" />
                  <Stack gap="4px">
                    <Text fontSize="14px" fontWeight="medium">
                      The interview feature is not available on the free plan.
                    </Text>
                  </Stack>
                </Flex>
              ) : (
                isExhausted && (
                  <Flex
                    bg="error.50"
                    color="error.500"
                    gap="12px"
                    mb="8px"
                    w="100%"
                    p="12px"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="error.200"
                    alignItems="center"
                  >
                    <WarningCircle size={24} weight="fill" />
                    <Stack gap="4px">
                      <Text fontSize="14px" fontWeight="medium">
                        You have exhausted your trial plan.
                      </Text>
                    </Stack>
                  </Flex>
                )
              )}
              {plan === AgencyPlan.TRIAL && (
                <Flex
                  bg="warning.50"
                  color="warning.500"
                  gap="12px"
                  mb="8px"
                  w="100%"
                  p="12px"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="warning.200"
                  alignItems="center"
                >
                  <WarningCircle size={24} weight="fill" />
                  <Stack gap="4px">
                    <Text fontSize="14px" fontWeight="medium">
                      Trial Plan Limitations: 1. Maximum {MAX_MEETING_TIME_PER_MEETING} minutes per
                      meeting. 2. Total meeting time: {MAX_MEETING_TIME_TOTAL} minutes.
                    </Text>
                  </Stack>
                </Flex>
              )}
              {/* Session Time Section */}
              <Flex gap="16px" justifyContent="flex-start">
                <Field
                  flex="2"
                  label={<RequiredFieldLabel>Session Time</RequiredFieldLabel>}
                  invalid={
                    !!errors.meeting_date || !!errors.meeting_time_from || !!errors.meeting_time_to
                  }
                  errorText={
                    <ErrorMessage
                      message={
                        errors.meeting_date?.message ||
                        errors.meeting_time_from?.message ||
                        errors.meeting_time_to?.message
                      }
                    />
                  }
                >
                  <Flex w="100%" gap="8px">
                    <Box w="100%">
                      <DatePicker
                        disabled={
                          isSubmitting ||
                          ((mode === 'add' || mode === 'add-candidate') && isLive) ||
                          planNotAvailable
                        }
                        selected={watch('meeting_date')}
                        onChange={(date: Date | null) => date && setValue('meeting_date', date)}
                        placeholderText="Select date"
                        dateFormat="MMMM d, yyyy"
                        excludeDateIntervals={[
                          {
                            start: new Date(0),
                            end: new Date(new Date().setDate(new Date().getDate() - 1))
                          }
                        ]}
                        autoFocus={false}
                        customInput={
                          <Input
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                            size="lg"
                            borderRadius="8px"
                            autoFocus={false}
                            placeholder="Select date"
                            aria-label="Meeting date"
                            {...register('meeting_date', {
                              required: 'Meeting date is required'
                            })}
                          />
                        }
                      />
                    </Box>
                    <Flex>
                      <TimeLabel>From</TimeLabel>
                      <Box w="100%">
                        <DatePicker
                          disabled={
                            isSubmitting ||
                            ((mode === 'add' || mode === 'add-candidate') && isLive) ||
                            planNotAvailable
                          }
                          selected={watch('meeting_time_from')}
                          onChange={(date: Date | null) => {
                            if (date) {
                              setValue('meeting_time_from', date);
                              setValue('meeting_time_to', addMinutes(date, 60));
                            }
                          }}
                          showTimeSelect
                          showTimeSelectOnly
                          placeholderText="Select time"
                          dateFormat="h:mm aa"
                          autoFocus={false}
                          customInput={
                            <Input
                              _disabled={{
                                bg: 'neutrals.300'
                              }}
                              w="120px"
                              size="lg"
                              borderRightRadius="8px"
                              borderLeftRadius="0px"
                              autoFocus={false}
                              placeholder="Select time"
                              aria-label="Meeting start time"
                              {...register('meeting_time_from', {
                                required: 'Start time is required'
                              })}
                            />
                          }
                        />
                      </Box>
                    </Flex>
                    <Flex>
                      <TimeLabel>To</TimeLabel>
                      <Box w="100%">
                        <DatePicker
                          disabled={
                            isSubmitting ||
                            ((mode === 'add' || mode === 'add-candidate') && isLive) ||
                            planNotAvailable
                          }
                          selected={watch('meeting_time_to')}
                          onChange={(date: Date | null) =>
                            date && setValue('meeting_time_to', date)
                          }
                          showTimeSelect
                          showTimeSelectOnly
                          placeholderText="Select time"
                          dateFormat="h:mm aa"
                          autoFocus={false}
                          customInput={
                            <Input
                              _disabled={{
                                bg: 'neutrals.300'
                              }}
                              size="lg"
                              w="120px"
                              borderRightRadius="8px"
                              borderLeftRadius="0px"
                              autoFocus={false}
                              placeholder="Select time"
                              aria-label="Meeting end time"
                              {...register('meeting_time_to', {
                                required: 'End time is required'
                              })}
                            />
                          }
                        />
                      </Box>
                    </Flex>
                  </Flex>
                </Field>

                {/* Timezone Field */}
                <Field
                  flex="1"
                  label={<Box fontSize="16px">Timezone</Box>}
                  invalid={!!errors.timezone}
                  errorText={<ErrorMessage message={errors.timezone?.message} />}
                >
                  <Controller
                    name="timezone"
                    control={control}
                    rules={{ required: 'Timezone is required' }}
                    render={({ field }) => (
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <Button
                            variant="outline"
                            size="lg"
                            borderColor={errors.timezone ? 'error.500' : 'neutrals.200'}
                            borderRadius="8px"
                            disabled={
                              isSubmitting ||
                              ((mode === 'add' || mode === 'add-candidate') && isLive) ||
                              planNotAvailable
                            }
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                            w="280px"
                            aria-label="Select timezone"
                          >
                            <Flex justifyContent="space-between" alignItems="center" w="100%">
                              <Box
                                fontSize="16px"
                                color="neutrals.700"
                                overflow="hidden"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                              >
                                {field.value.value.label || 'Select timezone'}
                              </Box>
                              <Box as="span" ml="8px" mr="0">
                                <CaretDown size={16} aria-hidden="true" />
                              </Box>
                            </Flex>
                          </Button>
                        </Menu.Trigger>
                        <Menu.Positioner>
                          <Menu.Content minW="10rem" maxH="300px" overflowY="auto">
                            <Menu.RadioItemGroup
                              value={field.value?.value.value || ''}
                              onValueChange={(value) => {
                                const selectedTimezone = parseTimezone(value.value);
                                field.onChange({ value: selectedTimezone });
                              }}
                            >
                              {options.map(({ value, label }) => (
                                <Menu.RadioItem
                                  key={value}
                                  value={value}
                                  fontSize="16px"
                                  _hover={{
                                    cursor: 'pointer',
                                    bg: 'neutrals.100'
                                  }}
                                >
                                  {label}
                                </Menu.RadioItem>
                              ))}
                            </Menu.RadioItemGroup>
                          </Menu.Content>
                        </Menu.Positioner>
                      </Menu.Root>
                    )}
                  />
                </Field>
              </Flex>

              {/* Live Meeting Checkbox - Only in add mode */}
              {(mode === 'add' || mode === 'add-candidate') && (
                <Controller
                  control={control}
                  name="isLive"
                  render={({ field }) => (
                    <Field
                      invalid={!!errors.isLive}
                      disabled={field.disabled || planNotAvailable}
                      errorText={<ErrorMessage message={errors.isLive?.message} />}
                    >
                      <Checkbox.Root
                        checked={field.value}
                        onCheckedChange={({ checked }) => field.onChange(checked)}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>
                          <Text fontSize="16px">This session is happening now</Text>
                        </Checkbox.Label>
                      </Checkbox.Root>
                    </Field>
                  )}
                />
              )}

              {/* Session Title Field */}
              <Field
                label={<Box fontSize="16px">Session Name</Box>}
                invalid={!!errors.title}
                errorText={<ErrorMessage message={errors.title?.message} />}
              >
                <Input
                  size="lg"
                  borderRadius="8px"
                  placeholder="Interview with John"
                  disabled={isSubmitting || planNotAvailable}
                  _disabled={{
                    bg: 'neutrals.300'
                  }}
                  {...register('title')}
                  aria-label="Meeting title"
                />
              </Field>

              {/* Meeting Link Field */}
              <Field
                label={<RequiredFieldLabel>Session Link</RequiredFieldLabel>}
                invalid={!!errors.meeting_url}
                errorText={<ErrorMessage message={errors.meeting_url?.message} />}
              >
                <Text fontSize="14px" color="neutrals.500">
                  Add meetings links from Zoom, Google Meet, and Microsoft Teams.
                </Text>
                <Group attached w="100%">
                  <InputAddon fontSize="16px" bg="background.secondary" borderRadius="15px">
                    <LinkSimpleHorizontal size={24} aria-hidden="true" />
                  </InputAddon>
                  <Input
                    disabled={isSubmitting || planNotAvailable}
                    size="lg"
                    placeholder="https://www.zoom.us/meeting-code"
                    w="100%"
                    type="text"
                    borderRadius="15px"
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    aria-label="Meeting URL"
                    {...register('meeting_url', {
                      required: 'Meeting link is required',
                      pattern: {
                        value: new RegExp(
                          `${ZOOM_URL_REGEX.source}|${GOOGLE_MEET_URL_REGEX.source}|${MS_TEAMS_URL_REGEX.source}`
                        ),
                        message: 'Please enter a valid Zoom, Google Meet, or Microsoft Teams link'
                      }
                    })}
                  />
                </Group>
              </Field>

              {/* Client Selection Field - Only in add mode */}
              <Field
                label={<RequiredFieldLabel>Client</RequiredFieldLabel>}
                invalid={!!errors.client}
                errorText={<ErrorMessage message={errors.client?.message} />}
              >
                <Controller
                  name="client"
                  control={control}
                  rules={{ required: 'Client is required' }}
                  render={({ field }) => (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          borderColor={errors.client ? 'error.500' : 'neutrals.200'}
                          borderRadius="8px"
                          disabled={
                            isSubmitting ||
                            isLoadingCandidate ||
                            mode === 'edit' ||
                            mode === 'add-candidate' ||
                            planNotAvailable ||
                            clients.length === 0
                          }
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                          w="100%"
                          aria-label="Select client"
                        >
                          <Flex justifyContent="space-between" alignItems="center" w="100%">
                            <Box
                              fontSize="16px"
                              color="neutrals.700"
                              overflow="hidden"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              display="flex"
                              alignItems="center"
                            >
                              {isLoadingCandidate ? (
                                <Flex gap="12px" alignContent="center">
                                  <Spinner w="16px" h="16px" mt="4px" />
                                  <Text fontSize="16px" mr="8px">
                                    Loading clients...
                                  </Text>
                                </Flex>
                              ) : field.value ? (
                                `${field.value.value.unique_id} - ${field.value.value.name}`
                              ) : (
                                'Select client'
                              )}
                            </Box>
                            <Box as="span" ml="8px" mr="0">
                              <CaretDown size={16} aria-hidden="true" />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content
                          minW="20rem"
                          w="100%"
                          position="fixed"
                          zIndex={1500}
                          maxH="300px"
                          overflowY="auto"
                        >
                          <Menu.RadioItemGroup
                            value={field.value?.value.client_id || ''}
                            onValueChange={(value) => {
                              const selectedClient = clients.find(
                                (client) => client.client_id === value.value
                              );
                              field.onChange({ value: selectedClient });
                              setJobs(selectedClient?.jobs || []);
                              setValue('job', undefined);
                              setValue('candidate', undefined);
                            }}
                          >
                            {clients.map((client) => (
                              <Menu.RadioItem
                                key={client.client_id}
                                value={client.client_id}
                                fontSize="16px"
                                _hover={{
                                  cursor: 'pointer',
                                  bg: 'neutrals.100'
                                }}
                              >
                                {client.unique_id} - {client.name}
                              </Menu.RadioItem>
                            ))}
                          </Menu.RadioItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>

              {/* Job Selection Field - Only in add mode and when client is selected */}
              {(selectedClient || mode === 'edit' || mode === 'add-candidate') && (
                <Field
                  label={<RequiredFieldLabel>Job</RequiredFieldLabel>}
                  invalid={!!errors.job}
                  errorText={<ErrorMessage message={errors.job?.message} />}
                >
                  <Controller
                    name="job"
                    control={control}
                    rules={{ required: 'Job is required' }}
                    render={({ field }) => (
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <Button
                            variant="outline"
                            size="lg"
                            borderColor={errors.job ? 'error.500' : 'neutrals.200'}
                            borderRadius="8px"
                            disabled={
                              isSubmitting ||
                              mode === 'edit' ||
                              mode === 'add-candidate' ||
                              planNotAvailable
                            }
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                            w="100%"
                            aria-label="Select job"
                          >
                            <Flex justifyContent="space-between" alignItems="center" w="100%">
                              <Box
                                fontSize="16px"
                                color="neutrals.700"
                                overflow="hidden"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                              >
                                {field.value
                                  ? `${field.value.value.unique_id} - ${field.value.value.job_title}`
                                  : 'Select job'}
                              </Box>
                              <Box as="span" ml="8px" mr="0">
                                <CaretDown size={16} aria-hidden="true" />
                              </Box>
                            </Flex>
                          </Button>
                        </Menu.Trigger>
                        <Menu.Positioner>
                          <Menu.Content
                            minW="20rem"
                            position="fixed"
                            zIndex={1500}
                            maxH="300px"
                            overflowY="auto"
                          >
                            <Menu.RadioItemGroup
                              value={field.value?.value.job_id || ''}
                              onValueChange={(value) => {
                                const selectedJob = jobs.find((job) => job.job_id === value.value);
                                field.onChange({ value: selectedJob });
                                setCandidates(selectedJob?.candidates || []);
                                setValue('candidate', undefined);
                              }}
                            >
                              {jobs.map((job) => (
                                <Menu.RadioItem
                                  key={job.job_id}
                                  value={job.job_id}
                                  fontSize="16px"
                                  _hover={{
                                    cursor: 'pointer',
                                    bg: 'neutrals.100'
                                  }}
                                >
                                  {job.job_title}
                                </Menu.RadioItem>
                              ))}
                            </Menu.RadioItemGroup>
                          </Menu.Content>
                        </Menu.Positioner>
                      </Menu.Root>
                    )}
                  />
                </Field>
              )}

              {/* Candidate Selection Field - Only in add mode and when job is selected */}
              {(selectedJob || mode === 'edit' || mode === 'add-candidate') && (
                <Field
                  label={<RequiredFieldLabel>Candidate</RequiredFieldLabel>}
                  invalid={!!errors.candidate}
                  errorText={<ErrorMessage message={errors.candidate?.message} />}
                >
                  <Controller
                    name="candidate"
                    control={control}
                    rules={{ required: 'Candidate is required' }}
                    render={({ field }) => (
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <Button
                            variant="outline"
                            size="lg"
                            borderColor={errors.candidate ? 'error.500' : 'neutrals.200'}
                            borderRadius="8px"
                            disabled={
                              isSubmitting ||
                              mode === 'edit' ||
                              mode === 'add-candidate' ||
                              planNotAvailable
                            }
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                            w="100%"
                            aria-label="Select candidate"
                          >
                            <Flex justifyContent="space-between" alignItems="center" w="100%">
                              <Box
                                fontSize="16px"
                                color="neutrals.700"
                                overflow="hidden"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                              >
                                {field.value
                                  ? getCandidateFullName(
                                      field.value.value.first_name,
                                      field.value.value.last_name
                                    )
                                  : 'Select candidate'}
                              </Box>
                              <Box as="span" ml="8px" mr="0">
                                <CaretDown size={16} aria-hidden="true" />
                              </Box>
                            </Flex>
                          </Button>
                        </Menu.Trigger>
                        <Menu.Positioner>
                          <Menu.Content
                            minW="20rem"
                            w="30rem"
                            position="fixed"
                            zIndex={1500}
                            maxH="300px"
                            overflowY="auto"
                          >
                            <Menu.RadioItemGroup
                              value={field.value?.value.candidate_id || ''}
                              onValueChange={(value) => {
                                const selectedCandidate = candidates.find(
                                  (candidate) => candidate.candidate_id === value.value
                                );
                                field.onChange({ value: selectedCandidate });
                              }}
                            >
                              {candidates.map((candidate) => (
                                <Menu.RadioItem
                                  key={candidate.candidate_id}
                                  value={candidate.candidate_id}
                                  fontSize="16px"
                                  _hover={{
                                    cursor: 'pointer',
                                    bg: 'neutrals.100'
                                  }}
                                >
                                  {getCandidateFullName(candidate.first_name, candidate.last_name)}{' '}
                                  - {candidate.email}
                                </Menu.RadioItem>
                              ))}
                            </Menu.RadioItemGroup>
                          </Menu.Content>
                        </Menu.Positioner>
                      </Menu.Root>
                    )}
                  />
                </Field>
              )}
            </Stack>

            {noCandidates && (
              <Box color="error.500" fontSize="16px" mt="16px" role="alert">
                No candidates available for scheduling. Please add candidates to the pipeline first.
              </Box>
            )}

            {/* Error Message */}
            {isError && (
              <Box color="error.700" fontSize="16px" mt="16px" role="alert">
                {isError}
              </Box>
            )}
          </DialogBody>

          {/* Dialog Footer */}
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button
                w="160px"
                borderColor="primary.900"
                background="background.secondary"
                color="primary.900"
                borderRadius="8px"
                fontSize="16px"
                disabled={isSubmitting}
                aria-label="Cancel"
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              type="submit"
              w="160px"
              background="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              fontSize="16px"
              loading={isSubmitting}
              disabled={isLoadingCandidate || isExhausted || planNotAvailable || noCandidates}
              aria-label="Submit"
            >
              {mode === 'add' || mode === 'add-candidate' ? 'Schedule Copilot' : 'Update'}
            </Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger m="12px" disabled={isSubmitting}>
          <X size={32} weight="bold" aria-label="Close dialog" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
    <LimitExhaustedDialog
      isOpen={isLimitDialogOpen}
      onClose={() => setIsLimitDialogOpen(false)}
      isFreePlan={isFreePlan}
      usageType="copilot"
    />
    </>
  );
}
